import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Loader2, Target, CalendarDays } from 'lucide-react';
import { Card, CardHeader, CardContent } from '@components/Card';
import { formatCurrencyBRL } from '@pages/financial/financialUtils';
import {
  ChartTooltip,
  kFormatter,
  getMonthOptions,
  getYearOptions,
} from '@pages/financialReports/financialReportsShared';
import {
  salesReportService,
  SalesGoalDailyReport,
  SalesGoalMonthlyReport,
} from '../../services/salesReportService';

const MONTHS_SHORT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const MONTHS_LONG = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const monthLabel = (year: number, month: number) => `${MONTHS_SHORT[month - 1]}/${String(year).slice(-2)}`;

// Colors for the per-channel stacked bars — excludes emerald (#10b981) and red (#ef4444),
// which are reserved for the accumulated-sales and accumulated-goal lines.
const CHANNEL_BAR_COLORS = ['#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#64748b', '#0ea5e9', '#a855f7'];

// ─── Daily chart (current month) ────────────────────────────────

const DailyGoalChart: React.FC = () => {
  const now = new Date();
  const [report, setReport] = useState<SalesGoalDailyReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        setReport(await salesReportService.getGoalDailyReport(now.getFullYear(), now.getMonth() + 1));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar');
        setReport(null);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One row per day feeding both charts. For each channel we emit the daily value
  // (`ch_<id>`, the stacked-bar segments in the bar chart) and its running total
  // (`chAcc_<id>`, the per-channel lines in the accumulated chart). The goal only
  // accumulates on business days, which is automatic because the backend already
  // sets `goal = 0` on non-business days.
  const chartData = useMemo(() => {
    if (!report) return [];
    let accumulatedGoal = 0;
    const channelAcc: Record<number, number> = {};
    return report.days.map(d => {
      accumulatedGoal += d.goal;
      const row: Record<string, number | string> = {
        day: d.day,
        accumulated: d.accumulated_sales,
        accumulatedGoal,
      };
      report.channels.forEach(ch => {
        const id = ch.sales_channel_id;
        const daily = d.channel_values?.[String(id)] ?? 0;
        channelAcc[id] = (channelAcc[id] ?? 0) + daily;
        row[`ch_${id}`] = daily; // segmento diário da barra empilhada
        row[`chAcc_${id}`] = channelAcc[id]; // linha acumulada do canal
      });
      return row;
    });
  }, [report]);

  // KPIs "até o dia atual": the daily panel is always the current month, so the current day
  // is today's day-of-month (fallback to the last day if not found, e.g. viewing another month).
  const kpisToDate = useMemo(() => {
    if (!report || report.days.length === 0) {
      return { accumulatedGoal: 0, accumulatedSales: 0, diff: 0 };
    }
    const foundIdx = report.days.findIndex(d => d.day === now.getDate());
    const idx = foundIdx >= 0 ? foundIdx : report.days.length - 1;
    const accumulatedGoal = report.days.slice(0, idx + 1).reduce((sum, d) => sum + d.goal, 0);
    const accumulatedSales = report.days[idx]?.accumulated_sales ?? 0;
    return { accumulatedGoal, accumulatedSales, diff: accumulatedSales - accumulatedGoal };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  const monthName = report ? MONTHS_LONG[report.month - 1] : MONTHS_LONG[now.getMonth()];

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <CalendarDays size={18} className="text-emerald-600" />
            Vendas diárias — {monthName}/{report?.year ?? now.getFullYear()}
          </span>
        }
      />
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-slate-400" />
            <span className="ml-2 text-slate-500">Carregando...</span>
          </div>
        ) : error ? (
          <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
        ) : !report || report.days.length === 0 ? (
          <div className="text-center py-16 text-slate-400">Sem dados para o mês corrente.</div>
        ) : (
          <>
            {/* KPIs — todos na mesma linha em telas largas */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Meta do mês</p>
                <p className="text-lg font-semibold text-slate-800">{formatCurrencyBRL(report.monthly_goal)}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Meta diária (dia útil)</p>
                <p className="text-lg font-semibold text-slate-800">{formatCurrencyBRL(report.daily_goal)}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Dias úteis</p>
                <p className="text-lg font-semibold text-slate-800">{report.business_days}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Vendas acumuladas</p>
                <p className="text-lg font-semibold text-emerald-700">
                  {formatCurrencyBRL(report.days[report.days.length - 1]?.accumulated_sales ?? 0)}
                </p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Meta acumulada (até hoje)</p>
                <p className="text-lg font-semibold text-slate-800">{formatCurrencyBRL(kpisToDate.accumulatedGoal)}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Diferença (vendas − meta)</p>
                <p className={`text-lg font-semibold ${kpisToDate.diff >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                  {kpisToDate.diff >= 0 ? '+' : ''}{formatCurrencyBRL(kpisToDate.diff)}
                </p>
              </div>
            </div>

            {/* Gráfico 1 — linhas acumuladas: meta, faturamento total e por canal */}
            <p className="text-sm font-medium text-slate-600 mb-2">Acumulado no mês — meta, faturamento e canais</p>
            <div className="h-96 mb-8">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis tickFormatter={kFormatter} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="accumulatedGoal"
                    name="Meta acumulada"
                    stroke="#ef4444"
                    strokeWidth={2}
                    strokeDasharray="6 3"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="accumulated"
                    name="Faturamento acumulado"
                    stroke="#10b981"
                    strokeWidth={2.5}
                    dot={false}
                  />
                  {report.channels.map((ch, i) => (
                    <Line
                      key={ch.sales_channel_id}
                      type="monotone"
                      dataKey={`chAcc_${ch.sales_channel_id}`}
                      name={ch.sales_channel_name}
                      stroke={CHANNEL_BAR_COLORS[i % CHANNEL_BAR_COLORS.length]}
                      strokeWidth={1.5}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Gráfico 2 — barras diárias empilhadas por canal (total = soma dos canais) */}
            <p className="text-sm font-medium text-slate-600 mb-2">Vendas por dia — total e por canal</p>
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis tickFormatter={kFormatter} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip content={<ChartTooltip showTotal totalLabel="Total do dia" />} />
                  <Legend />
                  {report.channels.map((ch, i) => (
                    <Bar
                      key={ch.sales_channel_id}
                      stackId="sales"
                      dataKey={`ch_${ch.sales_channel_id}`}
                      name={ch.sales_channel_name}
                      fill={CHANNEL_BAR_COLORS[i % CHANNEL_BAR_COLORS.length]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Monthly comparison ─────────────────────────────────────────

const defaultPeriod = () => {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), 1);
  const start = new Date(end.getFullYear(), end.getMonth() - 11, 1);
  return {
    startMonth: start.getMonth() + 1,
    startYear: start.getFullYear(),
    endMonth: end.getMonth() + 1,
    endYear: end.getFullYear(),
  };
};

const MonthlyGoalReport: React.FC = () => {
  const [period, setPeriod] = useState(defaultPeriod);
  const [report, setReport] = useState<SalesGoalMonthlyReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const dateFrom = `${period.startYear}-${String(period.startMonth).padStart(2, '0')}`;
      const dateTo = `${period.endYear}-${String(period.endMonth).padStart(2, '0')}`;
      setReport(await salesReportService.getGoalMonthlyReport(dateFrom, dateTo));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const chartData = useMemo(() => {
    if (!report) return [];
    return report.items.map(it => ({
      label: monthLabel(it.year, it.month),
      salesValue: it.sales_value,
      goal: it.goal_value,
      hasGoal: it.has_goal,
      goalMet: it.goal_met,
    }));
  }, [report]);

  const monthOptions = getMonthOptions();
  const yearOptions = getYearOptions();

  const selectCls = 'border border-slate-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500';

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <Target size={18} className="text-emerald-600" />
            Meta x Realizado (mensal)
          </span>
        }
      />
      <CardContent>
        {/* Period filters */}
        <div className="flex flex-wrap items-end gap-4 mb-6">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1 uppercase tracking-wider">Início</label>
            <div className="flex gap-2">
              <select
                className={selectCls}
                value={period.startMonth}
                onChange={e => setPeriod(p => ({ ...p, startMonth: Number(e.target.value) }))}
              >
                {monthOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <select
                className={selectCls}
                value={period.startYear}
                onChange={e => setPeriod(p => ({ ...p, startYear: Number(e.target.value) }))}
              >
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1 uppercase tracking-wider">Fim</label>
            <div className="flex gap-2">
              <select
                className={selectCls}
                value={period.endMonth}
                onChange={e => setPeriod(p => ({ ...p, endMonth: Number(e.target.value) }))}
              >
                {monthOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <select
                className={selectCls}
                value={period.endYear}
                onChange={e => setPeriod(p => ({ ...p, endYear: Number(e.target.value) }))}
              >
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-slate-400" />
            <span className="ml-2 text-slate-500">Carregando...</span>
          </div>
        ) : error ? (
          <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
        ) : !report || report.items.length === 0 ? (
          <div className="text-center py-16 text-slate-400">Sem dados para o período selecionado.</div>
        ) : (
          <>
            {/* Chart */}
            <div className="h-80 mb-6">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis tickFormatter={kFormatter} tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend />
                  <Bar dataKey="salesValue" name="Vendas" radius={[3, 3, 0, 0]}>
                    {chartData.map((d, i) => (
                      <Cell key={i} fill={d.hasGoal ? (d.goalMet ? '#10b981' : '#ef4444') : '#94a3b8'} />
                    ))}
                  </Bar>
                  <Line type="monotone" dataKey="goal" name="Meta" stroke="#0f172a" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Legend note */}
            <div className="flex flex-wrap gap-4 mb-4 text-xs text-slate-500">
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-500" /> Meta atingida</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-red-500" /> Meta não atingida</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-slate-400" /> Sem meta cadastrada</span>
            </div>

            {/* Table */}
            <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-3 font-semibold text-slate-700">Mês</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-700">Vendas</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-700">Meta</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-700">% Atingido</th>
                    <th className="text-center px-4 py-3 font-semibold text-slate-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {report.items.map((it, idx) => {
                    const pct = it.has_goal && it.goal_value > 0 ? (it.sales_value / it.goal_value) * 100 : null;
                    return (
                      <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-2 font-medium text-slate-700">{MONTHS_LONG[it.month - 1]}/{it.year}</td>
                        <td className="px-4 py-2 text-right text-slate-700">{formatCurrencyBRL(it.sales_value)}</td>
                        <td className="px-4 py-2 text-right text-slate-500">{it.has_goal ? formatCurrencyBRL(it.goal_value) : '—'}</td>
                        <td className="px-4 py-2 text-right text-slate-700">{pct != null ? `${pct.toFixed(1)}%` : '—'}</td>
                        <td className="px-4 py-2 text-center">
                          {!it.has_goal ? (
                            <span className="text-slate-400 text-xs">Sem meta</span>
                          ) : it.goal_met ? (
                            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Atingida</span>
                          ) : (
                            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Não atingida</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Tab ────────────────────────────────────────────────────────

export const SalesReportGoalTab: React.FC = () => (
  <div className="space-y-6">
    <DailyGoalChart />
    <MonthlyGoalReport />
  </div>
);

export default SalesReportGoalTab;
